import { createCryptoCurrency } from '@0xtorch/core'
import { etherlink } from 'viem/chains'
import { type Client, createClient } from '../../client'
import type { Explorer } from '../../explorer'
import { createBlockscout } from '../../explorer'
import type { LowerHex } from '../../types'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'

/**
 * Etherlink のネイティブトークンとして利用する Tezos 通貨定義。
 */
const tezos = createCryptoCurrency({
  type: 'CryptoCurrency',
  id: 'tezos',
  name: 'Tezos',
  symbol: 'XTZ',
  market: {
    coingeckoId: 'tezos',
  },
  updatedAt: 0,
})

/**
 * Etherlink チェーン定義を作成する。
 *
 * @param params.explorerProxyUrl - Explorer API を中継するプロキシ URL
 * @returns Etherlink の EVM チェーン定義
 */
export const createEtherlinkChain = ({
  explorerProxyUrl: proxyUrl,
}: {
  explorerProxyUrl?: string
}): Chain =>
  createEtherlinkChainCustom({
    client: createClient({
      chain: etherlink,
      httpRpcs: etherlinkHttpRpcs,
    }),
    explorer: createBlockscout({
      useAddressApiV2: true,
      name: 'Etherlink Explorer',
      baseUrl: 'https://explorer.etherlink.com',
      apiBaseUrl: 'https://explorer.etherlink.com/api',
      proxyUrl,
      useAddressInternalTransactionsV2: true,
    }),
  })

type CreateEtherlinkChainParameters = {
  client: Client
  explorer: Explorer
}

/**
 * Etherlink チェーン定義を任意の client / explorer で作成する。
 *
 * @param params.client - EVM RPC クライアント
 * @param params.explorer - Explorer クライアント
 * @returns Etherlink の EVM チェーン定義
 */
export const createEtherlinkChainCustom = ({
  client,
  explorer,
}: CreateEtherlinkChainParameters): Chain => ({
  id: 42_793,
  network: 'etherlink',
  name: 'Etherlink',
  nativeToken: {
    name: 'Tez',
    symbol: 'XTZ',
    decimals: 18,
    currency: tezos,
  },
  wrappedTokenAddresses: new Set<LowerHex>([
    '0xc9b53ab2679f573e480d01e0f49e2b5cfb7a3eab',
  ]),
  explorer,
  client,
  blockTime: 4830,
  coingeckoId: 'etherlink',
  defillamaId: 'etherlink',
})

/**
 * Etherlink の HTTP RPC 候補。
 */
export const etherlinkHttpRpcs: HttpRpc[] = [
  {
    url: 'https://node.mainnet.etherlink.com',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://rpc.ankr.com/etherlink_mainnet',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
]

/**
 * Etherlink の WebSocket RPC 候補。
 */
export const etherlinkWebsocketRpcUrls: readonly string[] = []
